import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsActive as NotificationsActiveIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { formatRelativeTime } from '../utils/formatters';
import { notificationsService } from '../services/notificationsService';
import { useNotifications } from '../hooks/useNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import NotificationDetailModal from './NotificationDetailModal';

const PUSH_DENIED_DISMISSED_KEY = 'agendario_push_denied_dismissed';

/**
 * NotificationCenter - Sistema de notificacoes isolado
 * Funciona independente do resto do app, nunca quebra a UI
 * @param {Object} props
 * @param {boolean} props.autoOpen - Se true, abre o popover automaticamente ao montar
 * @param {function} props.onAutoOpened - Callback chamado apos abrir automaticamente
 */
export default function NotificationCenter({ autoOpen = false, onAutoOpened, onToast }) {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);
  const [deniedDismissed, setDeniedDismissed] = useState(
    () => localStorage.getItem(PUSH_DENIED_DISMISSED_KEY) === 'true'
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailNotificationId, setDetailNotificationId] = useState(null);

  // Push notifications hook
  const {
    pushSupported,
    permissionState,
    isSubscribed,
    isLoading: pushLoading,
    requestPermission,
  } = usePushNotifications();

  // Carregar contador inicial
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // Auto open se prop autoOpen for true (deep-link de email)
  useEffect(() => {
    if (autoOpen && !autoOpenHandled && buttonRef.current) {
      const timer = setTimeout(() => {
        setAnchorEl(buttonRef.current);
        loadNotifications();
        setAutoOpenHandled(true);
        if (onAutoOpened) {
          onAutoOpened();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, autoOpenHandled, onAutoOpened]);

  // Conectar ao SignalR
  const handleNewNotification = useCallback((notification) => {
    console.log('[NotificationCenter] New notification received:', notification);

    // Adicionar nova notificacao no topo
    setNotifications((prev) => [notification, ...prev]);

    // Incrementar contador
    setUnreadCount((prev) => prev + 1);

    // Exibir toast notification
    if (onToast) {
      onToast(notification);
    }

    // Notificacao OS-level e responsabilidade do Service Worker via Push.
    // Nao chamar new Notification() aqui para evitar duplicacao.
  }, [onToast]);

  const { isConnected } = useNotifications(handleNewNotification);

  const loadUnreadCount = async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
      setHasError(false);
    } catch {
      console.warn('[NotificationCenter] Error loading unread count');
      setHasError(true);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsService.getNotifications(0, 20);
      setNotifications(data);
      setHasError(false);
    } catch {
      console.warn('[NotificationCenter] Error loading notifications');
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();

    try {
      const success = await notificationsService.markAsRead(notificationId);

      if (success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      console.warn('[NotificationCenter] Error marking as read');
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.readAt) {
      try {
        await notificationsService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        console.warn('[NotificationCenter] Error marking as read on click');
      }
    }

    // If notification has rich body content, open detail modal (body is fetched on-demand via API)
    if (notification.hasBody) {
      handleClose();
      setDetailNotificationId(notification.id);
      setDetailModalOpen(true);
      return;
    }

    const actionUrl = notification.actionUrl || notification.data?.actionUrl;
    if (actionUrl) {
      handleClose();
      if (actionUrl.startsWith('/')) {
        navigate(actionUrl);
      } else {
        window.open(actionUrl, '_blank');
      }
    }
  };

  const handleActivatePush = async () => {
    await requestPermission();
  };

  const handleDismissDenied = () => {
    localStorage.setItem(PUSH_DENIED_DISMISSED_KEY, 'true');
    setDeniedDismissed(true);
  };

  const formatDate = formatRelativeTime;

  const open = Boolean(anchorEl);

  // Determinar se deve exibir banner de push
  const showDefaultBanner = pushSupported && permissionState === 'default';
  const showDeniedBanner = pushSupported && permissionState === 'denied' && !deniedDismissed;

  if (hasError) {
    return (
      <Tooltip title="Notificacoes temporariamente indisponiveis">
        <IconButton color="inherit" disabled>
          <NotificationsNoneIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={isConnected ? 'Notificacoes (conectado)' : 'Notificacoes'}>
        <IconButton ref={buttonRef} color="inherit" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error">
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            Notificacoes
          </Typography>
          {isConnected && (
            <Chip
              label="Ao vivo"
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: 'success.main',
                color: 'white',
                height: 20,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        {/* Banner: Convidar usuario a ativar push (estado default) */}
        {showDefaultBanner && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'info.lighter',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <NotificationsActiveIcon sx={{ color: 'primary.main', mt: 0.3, fontSize: 20 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary">
                  Fique por dentro dos seus agendamentos!
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                  Receba alertas em tempo real, mesmo com o navegador fechado.
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleActivatePush}
              disabled={pushLoading}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 'bold' }}
            >
              {pushLoading ? 'Ativando...' : 'Ativar notificacoes'}
            </Button>
          </Box>
        )}

        {/* Banner: Usuario bloqueou notificacoes (estado denied) */}
        {showDeniedBanner && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              '& .MuiAlert-message': { width: '100%' },
            }}
            action={
              <Button color="inherit" size="small" onClick={handleDismissDenied}>
                Entendi
              </Button>
            }
          >
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Notificacoes bloqueadas
            </Typography>
            <Typography variant="caption">
              Para reativar, clique no icone de cadeado na barra de endereco do navegador e permita notificacoes.
            </Typography>
          </Alert>
        )}

        <Divider />

        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              Nenhuma notificacao
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 500, overflow: 'auto' }}>
            {notifications.map((notification, index) => {
              const isUnread = !notification.readAt;

              return (
                <Box key={notification.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      isUnread && (
                        <Tooltip title="Marcar como lida">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 2,
                        bgcolor: isUnread ? 'action.hover' : 'transparent',
                        cursor: (notification.hasBody || notification.actionUrl || notification.data?.actionUrl) ? 'pointer' : 'default',
                      }}
                    >
                      {isUnread && (
                        <CircleIcon
                          sx={{
                            fontSize: 8,
                            color: 'primary.main',
                            mr: 1,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={isUnread ? 'bold' : 'normal'}
                            color={isUnread ? 'text.primary' : 'text.secondary'}
                          >
                            {notification.title || 'Notificacao'}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color={isUnread ? 'text.secondary' : 'text.disabled'}
                              sx={{ mt: 0.5 }}
                            >
                              {notification.summary || notification.message || 'Voce tem uma nova notificacao'}
                            </Typography>
                            {notification.hasBody && (
                              <Chip
                                icon={<ArticleIcon sx={{ fontSize: 14 }} />}
                                label="Ver detalhes"
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              {formatDate(notification.createdAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={handleClose}>
                Fechar
              </Button>
            </Box>
          </>
        )}
      </Popover>

      <NotificationDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailNotificationId(null);
        }}
        notificationId={detailNotificationId}
      />
    </>
  );
}
